import { useCallback, useEffect, useState } from 'react';
import { Badge } from '@mui/material';
import usePageTitle from '../../hooks/title.hook';
import MainFrame from '../../components/MainFrame/mainFrame';
import styles from './cart.module.css';
import Breadcrumb from '../../components/Breadcrumb';
import BaseTab, { BaseTabItem } from '../../components/BaseTab/BaseTab';
import ReceiveTab from './receiveTab';
import ReceiveAgainTab from './receiveAgainTab';
import RemoteScanTab from './remoteScanTab';
import DispatchTab from './dispatchTab';
import DisposalTab from './disposalTab';
import { useReservationCartGetCount } from '../../hooks/api/reservationCart.hook';
import { RequestType } from '../../services/http/reservationCart.api';
import { useMessageModal } from '../../hooks/modal.hook';
import { ApiError } from '../../services/http';
import { useAuthContext } from '../../store/auth.store';
import ControlCode from '../../utils/controlCode';
import { DocDetailCloseAreaClass } from '../../utils/document.utils';

function Component() {
  usePageTitle('依頼リスト');
  const [initalActiveTab, setInitalActiveTab] = useState<number | null>(null);
  const [receiveQuantity, setReceiveQuantity] = useState<number | null>(null);
  const [receiveAgainQuantity, setReceiveAgainQuantity] = useState<number | null>(null);
  const [remoteScanQuantity, setRemoteScanQuantity] = useState<number | null>(null);
  const [dispatchQuantity, setDispatchQuantity] = useState<number | null>(null);
  const [disposalQuantity, setDisposalQuantity] = useState<number | null>(null);
  const { request: reservationCartGetCountRequest } = useReservationCartGetCount();
  const openMessageModal = useMessageModal();
  const { hasPermission } = useAuthContext();

  const renderTitle = (title: string, quantity: number | null) => (
    <div className={styles.tabTitle}>
      {title}
      {
        Boolean(quantity) && (
          <Badge
            classes={{
              badge: styles.tabTitleBadge,
            }}
            max={999999}
            badgeContent={quantity}
            showZero
          />
        )
      }
    </div>
  );

  const fetchReservationCartGetCount = useCallback(async () => {
    try {
      const { cartCountList } = await reservationCartGetCountRequest({});
      const receiveCartCount = cartCountList.find(({ requestType }) => requestType === RequestType.RECEIVE);
      const receiveAgainCartCount = cartCountList.find(({ requestType }) => requestType === RequestType.RECEIVE_AGAIN);
      const remoteScanCartCount = cartCountList.find(({ requestType }) => requestType === RequestType.REMOTE_SCAN);
      const dispatchCartCount = cartCountList.find(({ requestType }) => requestType === RequestType.DISPATCH);
      const disposalCartCount = cartCountList.find(({ requestType }) => requestType === RequestType.DISPOSAL);

      setReceiveQuantity(receiveCartCount?.count || 0);
      setReceiveAgainQuantity(receiveAgainCartCount?.count || 0);
      setRemoteScanQuantity(remoteScanCartCount?.count || 0);
      setDispatchQuantity(dispatchCartCount?.count || 0);
      setDisposalQuantity(disposalCartCount?.count || 0);

      const tabCandidates = [
        {
          index: 0,
          canAccess: hasPermission(ControlCode.Receiving),
          hasItems: Boolean(receiveCartCount?.count),
        },
        {
          index: 1,
          canAccess: hasPermission(ControlCode.Receiving),
          hasItems: Boolean(receiveAgainCartCount?.count),
        },
        {
          index: 2,
          canAccess: hasPermission(ControlCode.Scan),
          hasItems: Boolean(remoteScanCartCount?.count),
        },
        {
          index: 3,
          canAccess: hasPermission(ControlCode.Warehousing),
          hasItems: Boolean(dispatchCartCount?.count),
        },
        {
          index: 4,
          canAccess: hasPermission(ControlCode.Destruction),
          hasItems: Boolean(disposalCartCount?.count),
        },
      ];

      const defaultTabs = [
        { index: 0, canAccess: hasPermission(ControlCode.Receiving) },
        { index: 2, canAccess: hasPermission(ControlCode.Scan) },
        { index: 3, canAccess: hasPermission(ControlCode.Warehousing) },
        { index: 4, canAccess: hasPermission(ControlCode.Destruction) },
      ];

      const initialTab = tabCandidates.find((tab) => tab.canAccess && tab.hasItems)?.index
        ?? defaultTabs.find((tab) => tab.canAccess)?.index
        ?? 0;

      setInitalActiveTab(initialTab);
    } catch (e) {
      openMessageModal((e as ApiError)?.message);
    }
  }, [openMessageModal, reservationCartGetCountRequest]);

  useEffect(() => {
    fetchReservationCartGetCount();
  }, [fetchReservationCartGetCount]);

  return (
    <MainFrame
      borderBox
    >
      <div className={styles.mainframe}>
        <div className={DocDetailCloseAreaClass}>
          <Breadcrumb crumbs={[{ label: document.title }]} />
        </div>
        <div className={styles.mainFrameBody}>
          <div className={styles.body}>
            {initalActiveTab != null && (
              <BaseTab isSmallHeader initalActiveTab={initalActiveTab} headerClassName={DocDetailCloseAreaClass}>
                <BaseTabItem title={renderTitle('新規入庫', receiveQuantity)} hidden={!hasPermission(ControlCode.Receiving)}>
                  <ReceiveTab total={receiveQuantity} fetchReservationCartGetCount={fetchReservationCartGetCount} />
                </BaseTabItem>
                <BaseTabItem title={renderTitle('再入庫', receiveAgainQuantity)} hidden={!hasPermission(ControlCode.Receiving)}>
                  <ReceiveAgainTab total={receiveAgainQuantity} fetchReservationCartGetCount={fetchReservationCartGetCount} />
                </BaseTabItem>
                <BaseTabItem title={renderTitle('リモートスキャン', remoteScanQuantity)} hidden={!hasPermission(ControlCode.Scan)}>
                  <RemoteScanTab total={remoteScanQuantity} fetchReservationCartGetCount={fetchReservationCartGetCount} />
                </BaseTabItem>
                <BaseTabItem title={renderTitle('出庫', dispatchQuantity)} hidden={!hasPermission(ControlCode.Warehousing)}>
                  <DispatchTab total={dispatchQuantity} fetchReservationCartGetCount={fetchReservationCartGetCount} />
                </BaseTabItem>
                <BaseTabItem title={renderTitle('廃棄', disposalQuantity)} hidden={!hasPermission(ControlCode.Destruction)}>
                  <DisposalTab total={disposalQuantity} fetchReservationCartGetCount={fetchReservationCartGetCount} />
                </BaseTabItem>
              </BaseTab>
            )}
          </div>
        </div>
      </div>
    </MainFrame>
  );
}

export default Component;
